import { Color } from 'three';
import type { MeshStandardMaterial } from 'three';

/**
 * Create a color-matched plain (untextured) material from a textured material.
 * Clones the source and strips all texture maps while preserving PBR scalar properties.
 * If the source material has a `userData.baseColor` (hex number), that color is applied;
 * otherwise the cloned color is kept as-is.
 *
 * @param texturedMat The fully textured source material
 * @returns A new material with no texture maps and a matching solid color
 */
export const createPlainMaterial = (
	texturedMat: MeshStandardMaterial
): MeshStandardMaterial => {
	const plain = texturedMat.clone();

	// Strip all texture maps
	plain.map = null;
	plain.normalMap = null;
	plain.emissiveMap = null;
	plain.roughnessMap = null;
	plain.metalnessMap = null;
	plain.aoMap = null;
	plain.alphaMap = null;
	plain.displacementMap = null;
	plain.bumpMap = null;

	// Apply base color if configured
	if (texturedMat.userData.baseColor != null) {
		plain.color = new Color(texturedMat.userData.baseColor);
	}

	// Without an emissive map, emissive produces a flat tint — zero it out
	plain.emissive = new Color(0x000000);
	plain.emissiveIntensity = 0;

	plain.needsUpdate = true;
	return plain;
};

/**
 * Build the per-face material set for a surround panel split by `splitExtrudeCaps`:
 * [inner cap, extruded sides, outward cap]. Only the outward cap carries the finish, so the
 * textured material is last -- the order the geometry groups and the CSG contract both expect.
 *
 * @param texturedMat The finish material; it becomes the outward cap material
 * @returns The three materials in geometry-group order
 */
export const createSurroundMaterialSet = (
	texturedMat: MeshStandardMaterial
): Array<MeshStandardMaterial> => [
	createPlainMaterial(texturedMat),
	createPlainMaterial(texturedMat),
	texturedMat
];
